import { Intent as BlueprintIntent } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  LoadingIcon,
  Modal,
  Selector,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  SHARE_ORG_ENDPOINT_DIALOG_CANCEL,
  SHARE_ORG_ENDPOINT_DIALOG_EMAILS,
  SHARE_ORG_ENDPOINT_DIALOG_EMAILS_ITEM,
  SHARE_ORG_ENDPOINT_DIALOG_ROLES,
  SHARE_ORG_ENDPOINT_DIALOG_ROLES_ITEM,
  SHARE_ORG_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { handleMutationResult } from 'src/ui/containers/organization/hooks/helpers';
import { useServerRoles } from 'src/ui/containers/organization/hooks/serverRoles';
import { queueMessage } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useSetMemberConnectionRolesMutation,
} from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { useOrganization } from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(
  DialogType.SHARE_ORGANIZATION_ENDPOINT
);

export const ShareOrganizationEndpointDialog: React.VFC = () => {
  const { id = '', org = '' } = useParams<{ id?: string; org?: string }>();
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(isOpenSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { connection } = useCurrentConnection(id, org);
  const invalidateConnection = useInvalidateConnection(
    org,
    connection?.id,
    connection?.index
  );
  const { membersByEmail } = useOrganization(org);

  const roleConnections = React.useMemo(
    () => (connection ? [connection as Connection] : []),
    [connection]
  );
  const rolesByConnectionId = useServerRoles(
    roleConnections,
    Boolean(isOpen && connection)
  );
  const rolesData = rolesByConnectionId[connection?.id || ''] || {};
  const roleItems = Array.from(rolesData.roles || []).map((role) => ({
    value: role,
    text: role,
  }));

  const memberItems = React.useMemo(
    () =>
      Object.values(membersByEmail).map<SelectItem<null>>((member) => ({
        value: member.email,
        text: `${member.firstName} ${member.lastName}`,
        subText: member.email,
      })),
    [membersByEmail]
  );

  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

  const {
    isLoading,
    mutate: setMemberConnectionRoles,
  } = useSetMemberConnectionRolesMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data, vars) => {
      invalidateConnection();

      const didSucceed = handleMutationResult(
        dispatch,
        data.setMemberConnectionRoles,
        copy.components.endpoints.shareOrgEndpointDialog.notifications.error,
        vars.input.emails,
        (res) => res.email
      );
      if (!didSucceed) {
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.endpoints.shareOrgEndpointDialog.notifications
              .success,
        })
      );
      handleClose();
    },
  });

  const handleClose = () => {
    dispatch(closedDialog());
    setSelectedEmails([]);
    setSelectedRoles([]);
  };

  const handleSubmit = () => {
    if (!selectedEmails.length || isLoading || !connection?.id) {
      return;
    }

    setMemberConnectionRoles({
      input: {
        connectionId: connection.id,
        emails: selectedEmails,
        org_domain: org,
        stardog_roles: selectedRoles.length ? selectedRoles : undefined,
      },
    });
  };

  const isSubmitDisabled = !selectedEmails.length || isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={SHARE_ORG_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.shareOrgEndpointDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={SHARE_ORG_ENDPOINT_DIALOG_SUBMIT}
            variant="primary-accent"
          >
            {copy.components.endpoints.shareOrgEndpointDialog.buttons.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.shareOrgEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <Body size="md">
          {copy.components.endpoints.shareOrgEndpointDialog.description}
        </Body>

        <Selector.Multi<null, SelectItem, SelectItem[]>
          chipInputProps={{
            className: 'endpoint-dialog-chip-selector',
            clearable: false,
            label: copy.components.endpoints.inputs.email.label,
            size: 'md',
            testId: SHARE_ORG_ENDPOINT_DIALOG_EMAILS,
            values: selectedEmails,
          }}
          filterable
          items={memberItems}
          itemTestId={SHARE_ORG_ENDPOINT_DIALOG_EMAILS_ITEM}
          onSelect={(selected) =>
            setSelectedEmails(selected.map((item) => item.value))
          }
          popoverProps={{
            portalClassName: 'endpoint-dialog-menu-portal',
          }}
          selected={selectedEmails.map((email) => ({
            value: email,
            text: email,
          }))}
        />

        <Selector.Multi<null, SelectItem, SelectItem[]>
          chipInputProps={{
            className: 'endpoint-dialog-chip-selector',
            clearable: false,
            label: copy.components.endpoints.inputs.roles.shareLabel,
            size: 'md',
            testId: SHARE_ORG_ENDPOINT_DIALOG_ROLES,
            values: selectedRoles,
          }}
          creatable
          createText={copy.components.endpoints.inputs.roles.create}
          filterable
          items={roleItems}
          itemTestId={SHARE_ORG_ENDPOINT_DIALOG_ROLES_ITEM}
          loading={rolesData.isLoading}
          onSelect={(selected) =>
            setSelectedRoles(selected.map((item) => item.value))
          }
          popoverProps={{
            portalClassName: 'endpoint-dialog-menu-portal',
          }}
          selected={selectedRoles.map((role) => ({ value: role, text: role }))}
        />
      </div>
    </Modal>
  );
};
